import { Router } from '@lightningjs/sdk'

import { Buttons } from '../../components/button/Buttons'
import {
  NavigationPointers,
  PointerOrientation,
  VerticalPointerSize,
} from '../../components/NavigationPointers/NavigationPointers'
import TvBrowseLayout, { BrowseLayoutStates } from '../../components/TvBrowseLayout/TvBrowseLayout'
import { transparent } from '../../constants/colors'
import { LEFT_SAFE_ZONE_MARGIN } from '../../constants/general'
import { AppConfig } from '../../services/appConfig'
import { MessageModelTypesEnum, TvCardTypesEnum } from '../../types/flo-enums'
import { TvRowsContainerModelItem } from '../../types/flo-experience'
import { captureScreenData } from '../../utils/segmentAnalytics'
import PageHandler from './pageHandler'

export default class CarouselsHandler extends PageHandler {
  protected _lastOffset: number = 0
  protected _lastCarouselIndex: number = 0
  protected _lastCarouselCardIndex: number = 0
  protected _lastCarouselOffset: number = 0
  protected _lastCarousels: TvRowsContainerModelItem[] = []

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: transparent,
      collision: true,
      flex: { paddingLeft: LEFT_SAFE_ZONE_MARGIN },
      TvBrowseLayout: {
        type: TvBrowseLayout,
      },
      NavigationPointers: {
        type: NavigationPointers,
        pointerOrientation: PointerOrientation.VERTICAL,
      },
    }
  }

  get tvBrowseLayout() {
    return this.tag('TvBrowseLayout') as TvBrowseLayout
  }

  get navigationPointers() {
    return this.tag('NavigationPointers') as NavigationPointers
  }

  static _states() {
    return [
      class NotReady extends this {
        $enter() {
          this.setLoadingAnimation(true)
        }

        _handleKey() {
          return true
        }

        $exit() {
          this.setLoadingAnimation(false)
          void captureScreenData(this.tvBrowseLayout.data?.analytics)
        }
      },
      class Ready extends this {
        $enter() {
          this.checkNavigationPointersVisibility()
          Router.focusPage()
        }

        _getFocused() {
          return this.tvBrowseLayout
        }
      },
      class InBackgroundByModal extends this {
        _handleKey() {
          this.focusActiveModal()
        }
      },
    ]
  }

  restoreFocus() {
    this.tvBrowseLayout.tvRowsContainer.currentOffset = this._lastOffset
    this.tvBrowseLayout.tvRowsContainer.currentCarouselIndex = this._lastCarouselIndex
    this.tvBrowseLayout.tvRowsContainer.currentContainer.currentCardIndex =
      this._lastCarouselCardIndex
    this.tvBrowseLayout.tvRowsContainer.currentContainer.setXAxis?.(this._lastCarouselOffset)

    if (this._lastCarouselIndex > 0) {
      this.tvBrowseLayout.spotlight.clearLivePreview()
      this.tvBrowseLayout.spotlight.visible = false
    }
  }

  historyState(params) {
    if (params) {
      this._lastOffset = params.previous_last_offset
      this._lastCarouselIndex = params.previous_last_carousel_index
      this._lastCarouselOffset = params.previous_last_carousel_offset
      this._lastCarouselCardIndex = params.previous_last_carousel_card_index
      this._lastCarousels = params.previous_last_carousels
    } else {
      return {
        previous_last_offset: this.tvBrowseLayout?.tvRowsContainer?.carouselsContainer?.y ?? 0,
        previous_last_carousel_index:
          this.tvBrowseLayout?.tvRowsContainer?.currentCarouselIndex ?? 0,
        previous_last_carousel_offset:
          this.tvBrowseLayout?.tvRowsContainer?.currentContainer?.scrollContainer?.x ?? 0,
        previous_last_carousel_card_index:
          this.tvBrowseLayout?.tvRowsContainer?.currentContainer?.currentCardIndex ?? 0,
        previous_last_carousels: this.tvBrowseLayout?.tvRowsContainer?.data ?? 0,
      }
    }
  }

  $upMovement() {
    this.tvBrowseLayout?.tvRowsContainer?._handleUp()
    this.checkNavigationPointersVisibility()
  }

  $downMovement() {
    this.tvBrowseLayout?.tvRowsContainer?._handleDown()
    this.checkNavigationPointersVisibility()
  }

  $nextPageLoadComplete() {
    this.checkNavigationPointersVisibility()
  }

  checkNavigationPointersVisibility() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    const isFirstContainerFocused = this.tvBrowseLayout?.tvRowsContainer?.isFirstContainerFocused
    const isLastContainerFocused = this.tvBrowseLayout?.tvRowsContainer?.isLastContainerFocused
    const isNavHub = this.tvBrowseLayout?.navHubHeader?.visible
    const isFavoriteHub = this.tvBrowseLayout?.isFavoriteHub
    const isEventHub = this.tvBrowseLayout?.isEventHub
    const hasVerticalLogo = !!this.tvBrowseLayout?.data?.spotlight?.vertical_logo
    const currentCarousel = this.tvBrowseLayout?.tvRowsContainer?.currentContainer
    const currentCarouselType =
      currentCarousel?.data?.items?.[0]?.type ?? currentCarousel?.data?.type

    if (isNavHub) {
      this.tvBrowseLayout.title.visible = isFirstContainerFocused || !isCursorVisible
    }

    // Since the spotlight for these hubs pushes the carousels too much down below, we need to use smaller arrows
    // for the navigation pointers, but just for the very first carousel. For the rest we can keep using the regular
    // ones. That way, the collision box exceptions can be applied equally throughout all vertical homes.
    if (isFavoriteHub) {
      this.navigationPointers.verticalPointerSize = isFirstContainerFocused
        ? VerticalPointerSize.MINI
        : VerticalPointerSize.STANDARD
    } else if (isEventHub) {
      this.navigationPointers.verticalPointerSize = isFirstContainerFocused
        ? VerticalPointerSize.SMALL
        : VerticalPointerSize.STANDARD
    }

    this.tvBrowseLayout?.tvRowsContainer?.carouselsContainer?.childList.forEach((carousel) => {
      if (carousel.navigationPointers) {
        carousel.navigationPointers.visible =
          carousel.carouselIndex === this.tvBrowseLayout?.tvRowsContainer?.currentCarouselIndex &&
          isCursorVisible

        carousel.checkNavigationPointersVisibility()
      }
    })

    this.adjustNavigationPointersCollisionBox()

    const isQuickLinksFirst =
      isFirstContainerFocused && currentCarouselType === TvCardTypesEnum.TvButton
    const isCarouselMessage = currentCarouselType === MessageModelTypesEnum.Message
    this.tvBrowseLayout.pointerBackButton.y = hasVerticalLogo ? 105 : 80
    this.tvBrowseLayout.pointerBackButton.visible =
      !this._isRoot && !isQuickLinksFirst && !isCarouselMessage && isCursorVisible

    this.navigationPointers.visible = isCursorVisible
    this.navigationPointers.showTopPointer = !isFirstContainerFocused && isCursorVisible
    this.navigationPointers.showBottomPointer = !isLastContainerFocused && isCursorVisible
    this.navigationPointers.bottomContainer.collision = !isLastContainerFocused && isCursorVisible
    this.navigationPointers.topContainer.collision = !isQuickLinksFirst

    this._refocus()
  }

  _handleScroll({ deltaY }) {
    this.tvBrowseLayout?.tvRowsContainer?.handleScroll({ deltaY })
    this.checkNavigationPointersVisibility()
  }

  $updateFocusByHover(element) {
    if (element.parent instanceof Buttons) {
      // Currently, the only place where the Buttons component is used is in the Spotlight.
      this.tvBrowseLayout?._setState(BrowseLayoutStates.SpotlightMetadata)
    } else {
      this.tvBrowseLayout?._setState(BrowseLayoutStates.TvRowsContainer)
    }

    this._refocus()
  }

  // Increases the height of elements that overflow the navigation pointers that prevent
  // the user from clicking the tiles, even when not being fully covered by the gradients,
  // for some very specific tile combinations on vertical homes
  adjustNavigationPointersCollisionBox() {
    const currentCarouselIndex = this.tvBrowseLayout?.tvRowsContainer?.currentCarouselIndex
    const currentCarousel = this.tvBrowseLayout?.tvRowsContainer?.getChild(currentCarouselIndex)
    const currentCarouselType = currentCarousel?.data?.items?.[0]?.type
    const previousCarousel = this.tvBrowseLayout?.tvRowsContainer?.getChild(
      currentCarouselIndex - 1,
    )
    const previousCarouselType = previousCarousel?.data?.items?.[0]?.type
    const nextCarousel = this.tvBrowseLayout?.tvRowsContainer?.getChild(currentCarouselIndex + 1)
    const nextCarouselType = nextCarousel?.data?.items?.[0]?.type

    const collisionBoxHeightsMap = {
      [TvCardTypesEnum.TvFavoriteCard]: {
        [TvCardTypesEnum.TvEventCard]: { top: 200, bottom: 0 },
      },
      [TvCardTypesEnum.TvBannerMessage]: {
        [TvCardTypesEnum.TvEventCard]: { top: 160, bottom: 0 },
      },
      [TvCardTypesEnum.TvNavigationCard]: {
        [TvCardTypesEnum.TvEventCard]: { top: 200, bottom: 0 },
        [TvCardTypesEnum.TvEntityCard]: { top: 100, bottom: 0 },
      },
      [TvCardTypesEnum.TvButton]: {
        [TvCardTypesEnum.TvFavoriteCard]: { top: 50, bottom: 200 },
        [TvCardTypesEnum.TvBannerMessage]: { top: 0, bottom: 200 },
        [TvCardTypesEnum.TvEventCard]: { top: 230, bottom: 200 },
        [TvCardTypesEnum.TvNavigationCard]: { top: 0, bottom: 200 },
      },
      [TvCardTypesEnum.TvEventCard]: {
        [TvCardTypesEnum.TvNavigationCard]: { top: 150, bottom: 0 },
      },
    }

    const heights = collisionBoxHeightsMap[currentCarouselType]?.[previousCarouselType]
    const isNavHub = this.tvBrowseLayout?.navHubHeader?.visible

    if (
      heights &&
      currentCarouselType === TvCardTypesEnum.TvFavoriteCard &&
      previousCarouselType === TvCardTypesEnum.TvEventCard &&
      nextCarouselType === TvCardTypesEnum.TvEntityCard
    ) {
      // Special override, most likely to be seen on FloCollege vertical home
      heights.bottom = 200
    } else if (
      heights &&
      isNavHub &&
      currentCarouselType === TvCardTypesEnum.TvNavigationCard &&
      previousCarouselType === TvCardTypesEnum.TvEventCard &&
      nextCarouselType === TvCardTypesEnum.TvEventCard
    ) {
      // Special override, most likely to be seen on FloRacing's NavHub
      heights.bottom = 100
    }

    const isLastContainerFocused = this.tvBrowseLayout?.tvRowsContainer?.isLastContainerFocused

    if (isLastContainerFocused) {
      this.navigationPointers.topOverflowCollisionBox.h = 130
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    } else if (heights) {
      this.navigationPointers.topOverflowCollisionBox.h = heights.top
      this.navigationPointers.bottomOverflowCollisionBox.h = heights.bottom
    } else {
      this.navigationPointers.topOverflowCollisionBox.h = 0
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    }
  }

  // Used specifically by the Menu to prevent the left arrow from causing conflicts with the mouse hovering detection
  toggleCurrentCarouselLeftNavigationPointer(show = false) {
    const currentCarousel = this.tvBrowseLayout?.tvRowsContainer?.currentContainer

    if (currentCarousel?.navigationPointers) {
      if (show) {
        currentCarousel.checkNavigationPointersVisibility()
      } else {
        currentCarousel.navigationPointers.showLeftPointer = false
      }
    }
  }

  _onCursorEnter() {
    this.checkNavigationPointersVisibility()
  }

  _onCursorLeave() {
    this.checkNavigationPointersVisibility()
    this.tvBrowseLayout?.tvRowsContainer?.currentContainer?.currentCard?._focus()
  }
}
